import { createApi, fakeBaseQuery, SkipToken } from "@reduxjs/toolkit/dist/query/react";
import { collection, doc, getDoc, getDocs, setDoc, query, where, deleteDoc, writeBatch } from "firebase/firestore";
import { db, functions } from "@/firebase";
import { httpsCallable } from "firebase/functions";
import { setUserAccess } from "@/features/auth/slice";
import { OrgAccess } from "@/types";
import { ApiError } from "@/types/api";


export interface TeamMember {
  id: string;
  role: "super-admin" | "admin" | "manager" | "rider";
  userEmail: string;
  userName: string;
  joinedAt?: any;
}

export interface PendingInvite {
  id: string;
  orgId: string;
  orgName: string;
  orgType: string;
  email: string;
  role: "admin" | "manager" | "rider";
  status: "pending" | "accepted" | "failed";
}

export const accessApi = createApi({
  reducerPath: "userAccess",
  baseQuery: fakeBaseQuery<ApiError>(),
  tagTypes: ["userAccess", "teamMembers", "pendingInvitations"],
  endpoints: (builder) => ({
    fetchAccess: builder.query<OrgAccess[], string | SkipToken>({
      queryFn: async (userId, { dispatch }) => {
        try {
          const collectionRef = collection(db, `users/${String(userId)}/access`);

          const response = await getDocs(collectionRef).then((querySnapshot) => {
            return querySnapshot.docs.reduce((acc, doc: any) => {
              return [...acc, { ...doc.data(), id: doc.id }];
            }, [] as OrgAccess[]);
          });

          if (response.length) {
            dispatch(setUserAccess(response));

            return {
              data: response,
            };
          }

          return {
            error: {
              status: 404,
              statusText: "Accesos no encontrados",
            },
          };
        } catch (error: any) {
          console.error("Error in fetchAccess:", error);
          return {
            error: {
              status: 500,
              statusText: error.message || "Error al obtener accesos",
            },
          };
        }
      },
      providesTags: ["userAccess"],
    }),
    createAccess: builder.mutation<OrgAccess, { organizationId: string; organizationTitle: string } | SkipToken>({
      queryFn: async ({ organizationId, organizationTitle }, { getState }) => {
        const { auth } = getState() as any;

        if (!auth?.user?.uid) {
          return {
            error: {
              status: 401,
              statusText: "No autorizado",
            },
          };
        }

        const docRef = doc(db, `users/${String(auth?.user?.uid)}/access/${String(organizationId)}`);

        try {
          await setDoc(docRef, {
            role: "admin",
            title: organizationTitle,
          });

          return {
            data: organizationId as any,
          };
        } catch {
          return {
            error: {
              status: 500,
              statusText: "Error al crear el acceso",
            },
          };
        }
      },
      invalidatesTags: ["userAccess"],
    }),
    fetchTeamMembers: builder.query<TeamMember[], string | SkipToken>({
      queryFn: async (organizationId) => {
        if (!organizationId) {
          return { data: [] };
        }
        try {
          const collectionRef = collection(db, `organizations/${organizationId}/access`);
          const querySnapshot = await getDocs(collectionRef);
          const members = querySnapshot.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          })) as TeamMember[];
          return { data: members };
        } catch (error: any) {
          return {
            error: {
              status: 500,
              statusText: error.message || "Error al obtener miembros del equipo",
            },
          };
        }
      },
      providesTags: ["teamMembers"],
    }),
    fetchPendingInvitations: builder.query<PendingInvite[], string | SkipToken>({
      queryFn: async (organizationId) => {
        if (!organizationId) {
          return { data: [] };
        }
        try {
          const q = query(
            collection(db, "invitations"),
            where("orgId", "==", organizationId),
            where("status", "in", ["pending", "failed"])
          );
          const querySnapshot = await getDocs(q);
          const invitations = querySnapshot.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          })) as PendingInvite[];
          return { data: invitations };
        } catch (error: any) {
          return {
            error: {
              status: 500,
              statusText: error.message || "Error al obtener invitaciones",
            },
          };
        }
      },
      providesTags: ["pendingInvitations"],
    }),
    sendInvite: builder.mutation<void, { email: string; role: string; orgId: string }>({
      queryFn: async ({ email, role, orgId }) => {
        try {
          const sendOrganizationInviteFunc = httpsCallable<{ email: string; role: string; orgId: string }, { success: boolean; invitationId: string }>(
            functions,
            "sendOrganizationInvite"
          );
          await sendOrganizationInviteFunc({ email, role, orgId });
          return { data: undefined };
        } catch (error: any) {
          return {
            error: {
              status: 500,
              statusText: error.message || "Error al enviar la invitación",
            },
          };
        }
      },
      invalidatesTags: ["pendingInvitations"],
    }),
    revokeInvitation: builder.mutation<void, { invitationId: string }>({
      queryFn: async ({ invitationId }) => {
        try {
          const docRef = doc(db, "invitations", invitationId);
          await deleteDoc(docRef);
          return { data: undefined };
        } catch (error: any) {
          return {
            error: {
              status: 500,
              statusText: error.message || "Error al revocar la invitación",
            },
          };
        }
      },
      invalidatesTags: ["pendingInvitations"],
    }),
    resendInvite: builder.mutation<void, { invitationId: string }>({
      queryFn: async ({ invitationId }) => {
        try {
          const resendFunc = httpsCallable<{ invitationId: string }, { success: boolean }>(
            functions,
            "resendOrganizationInvite"
          );
          await resendFunc({ invitationId });
          return { data: undefined };
        } catch (error: any) {
          return {
            error: {
              status: 500,
              statusText: error.message || "Error al reenviar la invitación",
            },
          };
        }
      },
    }),
    removeTeamMember: builder.mutation<void, { organizationId: string; userId: string }>({
      queryFn: async ({ organizationId, userId }) => {
        try {
          const batch = writeBatch(db);

          const organizationAccessRef = doc(
            db,
            `organizations/${organizationId}/access/${userId}`
          );

          const userAccessRef = doc(
            db,
            `users/${userId}/access/${organizationId}`
          );

          batch.delete(organizationAccessRef);
          batch.delete(userAccessRef);

          await batch.commit();

          return {
            data: undefined,
          };
        } catch (error: any) {
          console.error("Error al eliminar miembro:", error);

          return {
            error: {
              status: 500,
              statusText:
                error.message || "Error al eliminar acceso del usuario",
            },
          };
        }
      },
      invalidatesTags: ["teamMembers", "userAccess"],
    }),




    fetchInvitationById: builder.query<PendingInvite, string | SkipToken>({
      queryFn: async (invitationId) => {
        if (typeof invitationId !== "string") {
          return { data: undefined as any };
        }
        try {
          const docRef = doc(db, "invitations", invitationId);
          const docSnap = await getDoc(docRef);
          if (!docSnap.exists()) {
            return {
              error: {
                status: 404,
                statusText: "Invitación no encontrada",
              },
            };
          }
          return { data: { id: docSnap.id, ...docSnap.data() } as any };
        } catch (error: any) {
          return {
            error: {
              status: 500,
              statusText: error.message || "Error al obtener detalles de la invitación",
            },
          };
        }
      },
    }),
    acceptInvite: builder.mutation<void, { invitationId: string }>({
      queryFn: async ({ invitationId }) => {
        try {
          const acceptFunc = httpsCallable<{ invitationId: string }, { success: boolean }>(
            functions,
            "acceptOrganizationInvite"
          );
          await acceptFunc({ invitationId });
          return { data: undefined };
        } catch (error: any) {
          return {
            error: {
              status: 500,
              statusText: error.message || "Error al aceptar la invitación",
            },
          };
        }
      },
      invalidatesTags: ["userAccess"],
    }),
  }),
});

export const {
  useFetchAccessQuery,
  useLazyFetchAccessQuery,
  useCreateAccessMutation,
  useFetchTeamMembersQuery,
  useFetchPendingInvitationsQuery,
  useSendInviteMutation,
  useRevokeInvitationMutation,
  useResendInviteMutation,
  useRemoveTeamMemberMutation,
  useFetchInvitationByIdQuery,
  useAcceptInviteMutation,
} = accessApi;